"use client";
import React, { useEffect, useState } from 'react';
import Footer from '../components/Footer';
import useProfile from '../hooks/useProfile';
import { useRouter } from 'next/navigation';
import Skeleton from '../components/ui/Skeleton';
import {
  Badge,
  GradientText,
  ScrollReveal,
  StaggerGroup,
  StaggerItem,
} from '../../components/shared';
import {
  MegaphoneIcon,
  SearchIcon,
  WalletIcon,
  ClockIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  ArrowRightIcon,
} from '../../components/shared/icons';

type Fundraiser = {
  title: string;
  description?: string;
  target?: number;
  raised?: number;
  walletAddress?: string;
  owner?: string;
  createdAt?: string | Date;
  expiresAt?: string | Date;
  active?: boolean;
};

/**
 * Fundraisers page, restyled to match the rest of the Reqrium dashboard
 * (glass hero panel, gradient headline, shared icon/badge system). The
 * underlying feature is untouched: this is a wallet-scoped lookup tool, not
 * a marketplace, so unlike the client mockup's FundraisersPage (which
 * invents global stats like "$4.8M+ raised" and a curated "Featured
 * Campaigns" list) this restyle only dresses up the page's actual real
 * sections - the wallet search, the create CTA, and the real results
 * returned from /api/fundraisers/:walletAddress. No fabricated stats.
 *
 * The "Explore Campaigns" button links to /fundraisers/explore, the real
 * browse-all-campaigns page (built separately to cover the mockup's
 * FundraiserExplorePage, which this wallet-lookup tool was never meant to
 * replace).
 */
export default function FundraisersPage() {
  // profile currently unused here; listing is public
  useProfile();
  const router = useRouter();
  const [walletFilter, setWalletFilter] = useState('');
  const [fundraisers, setFundraisers] = useState<Fundraiser[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchAttempt, setSearchAttempt] = useState(0);

  useEffect(() => {
    const walletAddress = walletFilter.trim();
    if (!walletAddress) {
      setFundraisers([]);
      setLoadError(null);
      setLoadingList(false);
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      setLoadingList(true);
      setLoadError(null);
      try {
        const response = await fetch(`/api/fundraisers/${encodeURIComponent(walletAddress)}`, {
          signal: controller.signal,
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) throw new Error(data?.message || `Unable to load fundraisers (${response.status}).`);
        setFundraisers(Array.isArray(data?.fundraisers) ? data.fundraisers : []);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setFundraisers([]);
        setLoadError(error instanceof Error ? error.message : 'Unable to load fundraisers.');
      } finally {
        if (!controller.signal.aborted) setLoadingList(false);
      }
    }, 300);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [walletFilter, searchAttempt]);

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{
        backgroundColor: "#0A0F1C",
        backgroundImage:
          "radial-gradient(circle at top, rgba(248,113,113,0.07), transparent 55%), radial-gradient(circle at bottom, rgba(0,212,255,0.06), transparent 55%)",
      }}
    >
      <main id="content" className="flex-1 w-full flex flex-col items-center">
        {/* Hero */}
        <section aria-labelledby="fundraisers-heading" className="page-section relative w-full pt-24 pb-5 sm:pt-28 sm:pb-6">
          <div className="mx-auto max-w-5xl">
            <ScrollReveal>
              <div
                className="section-panel relative overflow-hidden rounded-[32px] border px-5 py-8 sm:px-10 sm:py-10"
                style={{
                  borderColor: "rgba(91,127,255,0.28)",
                  background: "linear-gradient(180deg, rgba(16,24,39,0.75), rgba(10,15,28,0.9))",
                  boxShadow:
                    "0 0 0 1px rgba(248,113,113,0.08) inset, 0 30px 70px -30px rgba(0,0,0,0.8), 0 0 60px -20px rgba(248,113,113,0.22)",
                }}
              >
                <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
                  <div
                    className="absolute -top-24 left-1/3 h-72 w-72 rounded-full opacity-40 blur-3xl"
                    style={{ background: "radial-gradient(circle, rgba(248,113,113,0.22), transparent 70%)" }}
                  />
                  <div
                    className="absolute bottom-0 right-0 h-72 w-72 rounded-full opacity-30 blur-3xl"
                    style={{ background: "radial-gradient(circle, rgba(0,212,255,0.18), transparent 70%)" }}
                  />
                </div>

                <div className="relative z-10">
                  <Badge
                    icon={<MegaphoneIcon size={14} />}
                    borderColor="rgba(248,113,113,0.4)"
                    backgroundColor="rgba(248,113,113,0.1)"
                    textColor="#fca5a5"
                  >
                    On-Chain Fundraising
                  </Badge>

                  <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
                    <div>
                      <h1 id="fundraisers-heading" className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
                        <GradientText variant="brand">Fundraisers</GradientText>
                      </h1>
                      <p className="mt-3 max-w-xl text-base sm:text-lg leading-relaxed" style={{ color: "var(--muted-text, #94a3b8)" }}>
                        Look up fundraising campaigns tied to a wallet address, or launch your own.
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 pb-1">
                      <button
                        onClick={() => router.push('/fundraisers/create')}
                        className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_0_24px_-8px_rgba(248,113,113,0.5)] transition hover:-translate-y-0.5"
                      >
                        Create Fundraiser
                        <ArrowRightIcon size={16} />
                      </button>
                      <button
                        onClick={() => router.push('/fundraisers/explore')}
                        className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold transition hover:-translate-y-0.5"
                        style={{ borderColor: 'rgba(248,113,113,0.4)', color: '#fca5a5', background: 'rgba(248,113,113,0.08)' }}
                      >
                        Explore Campaigns
                        <ArrowRightIcon size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  className="relative z-10 mt-8 flex items-center gap-3 rounded-2xl border px-4 py-3.5"
                  style={{ borderColor: "rgba(91,127,255,0.2)", background: "rgba(8,12,22,0.5)" }}
                >
                  <SearchIcon size={18} className="shrink-0" style={{ color: "#94a3b8" }} />
                  <input
                    value={walletFilter}
                    onChange={e => setWalletFilter(e.target.value)}
                    placeholder="Search by wallet address (0x...)"
                    aria-label="Search fundraisers by wallet address"
                    className="flex-1 bg-transparent text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none"
                  />
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        <div className="page-section w-full">
          <div className="mx-auto max-w-5xl mb-4">
            <button onClick={() => router.push('/')} className="text-sm font-medium text-cyan-300 hover:text-cyan-200 transition-colors">
              ← Return home
            </button>
          </div>
        </div>

        {/* Results */}
        <section aria-label="Fundraiser results" className="page-section relative w-full pb-16">
          <div className="mx-auto max-w-5xl">
            {loadError ? (
              <div
                role="alert"
                className="flex flex-col items-start justify-between gap-4 rounded-2xl border px-5 py-4 sm:flex-row sm:items-center"
                style={{ borderColor: "rgba(248,113,113,0.35)", background: "rgba(127,29,29,0.18)", color: "#fecaca" }}
              >
                <div className="flex items-start gap-3 text-sm">
                  <AlertTriangleIcon size={18} className="mt-0.5 shrink-0" />
                  <span>We could not load fundraisers for this wallet. {loadError}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setSearchAttempt(attempt => attempt + 1)}
                  className="shrink-0 rounded-full border px-4 py-2 text-xs font-semibold transition hover:bg-white/10"
                  style={{ borderColor: "rgba(254,202,202,0.45)", color: "#fecaca" }}
                >
                  Try again
                </button>
              </div>
            ) : loadingList ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" aria-label="Loading fundraisers">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="p-5 rounded-2xl border"
                    style={{
                      background: "var(--glass-bg, rgba(16,24,39,0.55))",
                      borderColor: "var(--color-card-border, rgba(91,127,255,0.18))",
                      backdropFilter: "blur(18px)",
                    }}
                  >
                    <Skeleton className="h-5 w-1/2 bg-slate-700/60 rounded mb-3" />
                    <Skeleton className="h-3 w-1/3 bg-slate-700/40 rounded mb-3" />
                    <Skeleton className="h-3 w-3/4 bg-slate-700/30 rounded" />
                  </div>
                ))}
              </div>
            ) : !walletFilter.trim() ? (
              <EmptyState
                icon={<SearchIcon size={22} style={{ color: "#94a3b8" }} />}
                text="Enter a wallet address above to find its fundraisers."
              />
            ) : fundraisers.length === 0 ? (
              <EmptyState
                icon={<AlertTriangleIcon size={22} style={{ color: "#fca5a5" }} />}
                text="No fundraisers found for this address."
              />
            ) : (
              <StaggerGroup className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {fundraisers.map((f, i) => (
                  <StaggerItem key={i}>
                    <FundraiserCard f={f} />
                  </StaggerItem>
                ))}
              </StaggerGroup>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function EmptyState({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div
      className="flex flex-col items-center gap-3 rounded-2xl border px-6 py-14 text-center"
      style={{
        background: "var(--glass-bg, rgba(16,24,39,0.55))",
        borderColor: "var(--color-card-border, rgba(91,127,255,0.18))",
      }}
    >
      {icon}
      <p className="text-sm" style={{ color: "var(--muted-text, #94a3b8)" }}>{text}</p>
    </div>
  );
}

function FundraiserCard({ f }: { f: Fundraiser }) {
  const target = f.target ?? 0;
  const raised = f.raised ?? 0;
  const pct = target > 0 ? Math.min(100, Math.round((raised / target) * 100)) : 0;
  const expires = f.expiresAt ? new Date(f.expiresAt) : null;
  const formattedExpires = expires && !isNaN(expires.getTime()) ? expires.toLocaleDateString() : null;

  return (
    <div
      className="group relative p-5 rounded-2xl border transition-all duration-300 hover:-translate-y-1"
      style={{
        background: "var(--glass-bg, rgba(16,24,39,0.55))",
        borderColor: "rgba(248,113,113,0.22)",
        backdropFilter: "blur(18px)",
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="font-semibold text-base" style={{ color: "#f1f5f9" }}>{f.title}</h2>
        {f.active ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: "rgba(34,197,94,0.12)", color: "#4ade80" }}>
            <CheckCircleIcon size={11} /> Active
          </span>
        ) : null}
      </div>

      {f.description ? (
        <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--muted-text, #94a3b8)" }}>
          {f.description}
        </p>
      ) : null}

      <div className="mt-4">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span style={{ color: "#fca5a5" }}>Raised {raised.toLocaleString()}</span>
          <span style={{ color: "var(--muted-text, #94a3b8)" }}>Goal {target.toLocaleString()}</span>
        </div>
        <div className="h-2 w-full rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
          <div
            className="h-full rounded-full bg-gradient-to-r from-red-500 to-orange-400"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px]" style={{ color: "var(--muted-text, #94a3b8)" }}>
        {f.walletAddress ? (
          <span className="inline-flex items-center gap-1">
            <WalletIcon size={13} />
            <span className="font-mono">{f.walletAddress.slice(0, 6)}...{f.walletAddress.slice(-4)}</span>
          </span>
        ) : null}
        {formattedExpires ? (
          <span className="inline-flex items-center gap-1">
            <ClockIcon size={13} />
            Ends {formattedExpires}
          </span>
        ) : null}
      </div>
    </div>
  );
}
